import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Modal } from "react-bootstrap";
import { HeartFill, XCircle } from "react-bootstrap-icons";
import { useDispatch } from "react-redux";
import { clearActiveExercise } from "../../../redux/slices/exerciseSlice";
import { getRandomSentence } from "../../../utils/getRandomSentence";
import "./BuildSentence.css";

export default function BuildSentence() {
  const [sentence, setSentence] = useState(""); // Holds the fetched sentence
  const [sentenceWords, setSentenceWords] = useState([]); // Holds the words of the sentence
  const [isExerciseComplete, setIsExerciseComplete] = useState(false); // Tracks exercise complitness state
  const [wordPool, setWordPool] = useState([]); // Holds the words moved from sentence to pool
  const [interactiveWordIndexes, setInteractiveWordIndexes] = useState([]); // Tracks which words are interactive
  const [remainingAttempts, setRemainingAttempts] = useState(3); // Tracks remaining attempts
  const [showCheckButton, setShowCheckButton] = useState(false); // Controls visibility of the Check button
  const [isCorrect, setIsCorrect] = useState([]); // Tracks if each word is correct
  const [showModal, setShowModal] = useState(false); // Controls the visibility of the modal
  const [correctCount, setCorrectCount] = useState(0); // Tracks the number of correct answers
  const [incorrectCount, setIncorrectCount] = useState(0); // Tracks the number of incorrect answers
  const dispatch = useDispatch();

  // Fetch a random sentence from the database
  const fetchSentence = async () => {
    try {
      const fetchedSentence = await getRandomSentence(); // Fetch sentence from DB
      setSentence(fetchedSentence.sentence); // Set the fetched sentence
      const wordsArray = fetchedSentence.sentence.split(" "); // Split sentence into words
      setSentenceWords(wordsArray); // Set the sentence words
      initializeWordPool(wordsArray); // Initialize the word pool by selecting 20% of words
    } catch (error) {
      console.error("Failed to fetch sentence:", error);
    }
  };

  // Fetch sentence on component mount
  useEffect(() => {
    fetchSentence(); // Fetch the sentence when component mounts
  }, []);

  // Initialize word pool with 20% of words from the sentence
  const initializeWordPool = (wordsArray) => {
    const numberOfWordsToMove = Math.floor(wordsArray.length * 0.2); // Calculate 20% of total words
    const wordIndexes = getRandomIndexes(
      wordsArray.length,
      numberOfWordsToMove
    ); // Get random indexes for words
    const selectedWords = wordIndexes.map((index) => wordsArray[index]); // Get words for the pool

    setWordPool(selectedWords); // Set the word pool with selected words
    setInteractiveWordIndexes(wordIndexes); // Track which word indexes are interactive
    setSentenceWords(
      wordsArray.map((word, index) =>
        wordIndexes.includes(index) ? null : word
      )
    ); // Replace selected words with null to indicate placeholders
  };

  // Function to get random unique indexes for the words to move to the word pool
  const getRandomIndexes = (length, count) => {
    const indexes = new Set(); // Use a Set to ensure unique indexes
    while (indexes.size < count) {
      indexes.add(Math.floor(Math.random() * length)); // Generate random unique indexes
    }
    return Array.from(indexes); // Convert Set to Array and return it
  };

  const handleDragStart = (e, word, from, index) => {
    e.dataTransfer.setData("word", word);
    e.dataTransfer.setData("from", from);
    e.dataTransfer.setData("index", index); // Send the index to track where the word came from
  };

  const handleDropInPool = (e) => {
    const word = e.dataTransfer.getData("word");
    const from = e.dataTransfer.getData("from");
    const index = parseInt(e.dataTransfer.getData("index"));

    if (from === "sentence") {
      moveWordToPool(word, index); // Move word from sentence to pool
    }
  };

  const handleDropInSentence = (e, targetIndex) => {
    const word = e.dataTransfer.getData("word");
    const from = e.dataTransfer.getData("from");
    const sourceIndex = parseInt(e.dataTransfer.getData("index"));

    if (from === "pool" && sentenceWords[targetIndex] === null) {
      moveWordToSentence(word, targetIndex); // Move the word from pool to the placeholder
    } else if (from === "sentence" && sentenceWords[targetIndex] === null) {
      // Move the word within the sentence (from one slot to another)
      swapSentenceWords(sourceIndex, targetIndex);
    }
  };

  const allowDrop = (e) => {
    e.preventDefault();
  };

  const moveWordToPool = (word, index) => {
    const updatedSentenceWords = [...sentenceWords];
    updatedSentenceWords[index] = null; // Replace the word in the sentence with a placeholder
    setSentenceWords(updatedSentenceWords);
    setWordPool([...wordPool, word]); // Add the word back to the pool
    setShowCheckButton(false); // Hide the check button if a word is removed
  };

  const moveWordToSentence = (word, index) => {
    setWordPool(wordPool.filter((w) => w !== word)); // Remove the word from the pool
    const updatedSentenceWords = [...sentenceWords];
    updatedSentenceWords[index] = word; // Place the word in the correct index
    setSentenceWords(updatedSentenceWords); // Update the sentence words state

    // Show the check button if all placeholders are filled
    if (updatedSentenceWords.every((word) => word !== null)) {
      setShowCheckButton(true);
    }
  };

  // Swap words within the sentence (from one slot to another)
  const swapSentenceWords = (sourceIndex, targetIndex) => {
    const updatedSentenceWords = [...sentenceWords];
    updatedSentenceWords[targetIndex] = updatedSentenceWords[sourceIndex]; // Move word to new slot
    updatedSentenceWords[sourceIndex] = null; // Leave the old slot empty
    setSentenceWords(updatedSentenceWords);
    setShowCheckButton(false); // Hide the check button if any word is swapped
  };

  const handleWordClick = (word, from, index) => {
    if (from === "sentence") {
      moveWordToPool(word, index); // Move word from sentence to pool on click
    } else {
      const firstEmptyIndex = sentenceWords.findIndex((word) => word === null);
      if (firstEmptyIndex !== -1) {
        moveWordToSentence(word, firstEmptyIndex); // Move word from pool to first available placeholder
      }
    }
  };

  // Check the sentence and highlight incorrect words
  const handleCheck = () => {
    const correctness = interactiveWordIndexes.map(
      (index) => sentenceWords[index] === sentence.split(" ")[index]
    );

    const correct = correctness.filter(Boolean).length;
    const incorrect = correctness.length - correct;

    setCorrectCount(correct); // Track the correct pool words
    setIncorrectCount(incorrect); // Track the incorrect pool words

    // New flag to track if the exercise is complete
    const isComplete = correct === interactiveWordIndexes.length;

    setIsCorrect((prevCorrect) =>
      sentenceWords.map((word, index) =>
        interactiveWordIndexes.includes(index)
          ? correctness[interactiveWordIndexes.indexOf(index)]
          : true
      )
    ); // Track correctness for each word in sentence, ignoring non-interactive words

    // Display modal with result
    setShowModal(true);

    // Check if exercise is complete
    if (isComplete) {
      setIsExerciseComplete(true);
    }
  };

  // Reset the entire exercise for a new sentence
  const handleResetExercise = async () => {
    await fetchSentence(); // Fetch new sentence
    setShowModal(false);
    setRemainingAttempts(3); // Reset remaining attempts
    setCorrectCount(0); // Reset correct count
    setIncorrectCount(0); // Reset incorrect count
    setShowCheckButton(false); // Hide check button for new sentence
    setIsCorrect([]); // Reset correctness tracking
  };

  // Try again with the remaining words and mistakes
  const handleTryAgain = () => {
    // Find incorrect words and reset them back to the pool
    const incorrectWords = sentenceWords.filter(
      (word, index) => !isCorrect[index] && word !== null
    );

    setWordPool([...wordPool, ...incorrectWords]); // Move incorrect words back to the pool

    // Reset incorrect words in the sentence
    setSentenceWords(
      sentenceWords.map((word, index) => (isCorrect[index] ? word : null))
    );

    // Reset correctness tracking so that incorrect words are not highlighted before the next check
    setIsCorrect([]);

    setShowModal(false); // Close the modal
    setShowCheckButton(false); // Hide the check button until the sentence is completed again
  };

  return (
    <>
      {/* Attempts indicator */}
      <div className="attempts-header d-flex justify-content-between pt-2">
        <div className="attempts-indicator">
          {Array.from({ length: remainingAttempts }).map((_, idx) => (
            <HeartFill key={idx} className="heart-icon" />
          ))}
        </div>
        <div>
          <XCircle
            className="x-icon"
            onClick={() => dispatch(clearActiveExercise())} // Clear the current exercise
          />
        </div>
      </div>
      <Container fluid className="build-sentence-container">
        {/* Main Content */}
        <Row className="justify-content-center align-items-center sentence-row">
          <Col xs={12} md={8} lg={6} className="text-center">
            <div className="sentence-container">
              {sentenceWords.map((word, index) =>
                word === null ? (
                  <span
                    key={index}
                    className="word-placeholder"
                    onDragOver={allowDrop}
                    onDrop={(e) => handleDropInSentence(e, index)} // Handle drop event for the placeholder
                  >
                    {/* Empty Placeholder */}
                  </span>
                ) : interactiveWordIndexes.includes(index) ? (
                  <span
                    key={index}
                    className={`sentence-word installed-word ${
                      isCorrect.length && !isCorrect[index]
                        ? "incorrect-word"
                        : ""
                    }`}
                    draggable
                    onDragStart={(e) =>
                      handleDragStart(e, word, "sentence", index)
                    }
                    onClick={() => handleWordClick(word, "sentence", index)} // Allow click to move back to pool
                  >
                    {word + " "}
                  </span>
                ) : (
                  <span
                    key={index}
                    className={`sentence-word non-interactive ${
                      isCorrect.length && !isCorrect[index]
                        ? "incorrect-word"
                        : ""
                    }`}
                  >
                    {word + " "}
                  </span>
                )
              )}
            </div>
          </Col>
        </Row>

        {/* Word Pool or Check Button */}
        {!showCheckButton ? (
          <Row className="justify-content-center align-items-center word-pool-row">
            <Col xs={12} md={8} lg={6} className="text-center">
              <div
                className="word-pool-container"
                onDragOver={allowDrop}
                onDrop={handleDropInPool} // Handle drop event for the word pool
              >
                {wordPool.map((word, index) => (
                  <span
                    key={index}
                    draggable
                    onDragStart={(e) => handleDragStart(e, word, "pool", index)}
                    onClick={() => handleWordClick(word, "pool")}
                    className="word-pool-item"
                  >
                    {word}
                  </span>
                ))}
              </div>
            </Col>
          </Row>
        ) : (
          <Row className="justify-content-center align-items-center check-row">
            <Col xs={12} md={8} lg={6} className="text-center">
              <Button variant="primary" onClick={handleCheck}>
                Check
              </Button>
            </Col>
          </Row>
        )}

        {/* Modal for completion results */}
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header closeButton={false}>
            <Modal.Title>Exercise Results</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {isExerciseComplete ? (
              <>Exercise complete! Would you like to try again?</>
            ) : remainingAttempts === 0 ? (
              <>
                Exercise finished! You have reached the maximum attempts. Would
                you like to try again from the beginning?
              </>
            ) : (
              <>
                You put {correctCount} words correctly. {incorrectCount}{" "}
                remaining, try again?
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {isExerciseComplete ? (
              <Button variant="secondary" onClick={handleResetExercise}>
                One more sentence {/* Exercise finished successfully */}
              </Button>
            ) : remainingAttempts > 0 ? (
              <Button variant="secondary" onClick={handleTryAgain}>
                Continue {/* Continue with the same sentence */}
              </Button>
            ) : (
              <Button variant="secondary" onClick={handleResetExercise}>
                Try Again {/* Start again with the new sentence */}
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => dispatch(clearActiveExercise())}
            >
              Return to Exercises
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
}
